<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载微信号">
    <div class="wx-page">
      <div class="page-head">
        <div class="title-block">
          <h2>微信号管理</h2>
          <p>一个账号可以绑定多个微信号，每个微信号可复用给多个伴侣</p>
        </div>
        <el-button type="primary" round class="add-btn" @click="openBind">
          <i class="el-icon-plus"></i> 扫码新增微信号
        </el-button>
      </div>

      <div v-if="!accounts.length" class="wx-empty">还没有绑定微信号，点击右上角「扫码新增微信号」开始绑定。</div>
      <div class="grid">
        <div v-for="acc in accounts" :key="acc.id" class="acc-card">
          <div class="acc-top">
            <img :src="acc.avatar || require('@/assert/主页人物.png')" alt="" class="av" />
            <div class="meta">
              <b>{{ acc.nickname }}</b>
              <span class="wxid">微信号 {{ acc.wechatId }}</span>
            </div>
            <span class="status" :class="acc.status">
              {{ acc.status === "online" ? "● 在线" : "● 离线" }}
            </span>
          </div>
          <div class="acc-info">
            <span>绑定于 {{ acc.boundAt }}</span>
            <span>最近活跃 {{ acc.lastSeen }}</span>
          </div>
          <div class="bound">
            <label>已绑定伴侣 ({{ acc.companions.length }})</label>
            <div v-if="acc.companions.length" class="chips">
              <span v-for="c in acc.companions" :key="c.id" class="chip">
                {{ c.name }}<em v-if="c.isDefault">默认</em>
              </span>
            </div>
            <p v-else class="empty">暂未绑定伴侣，可在创建伴侣时选择此微信号</p>
          </div>
          <div class="acc-btns">
            <el-button size="small" round @click="openDetail(acc)">查看详情</el-button>
            <el-button size="small" round class="danger" @click="confirmUnbind(acc)">解绑</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 扫码绑定弹窗 -->
    <el-dialog title="扫码新增微信号" :visible.sync="bindVisible" width="380px" custom-class="yw-dialog" @close="stopPolling">
      <div class="bind-body">
        <div class="qr-box">
          <img v-if="bindQr" :src="bindQr" alt="" class="qr" />
          <div v-else class="qr placeholder">
            <i :class="bindStep === 'error' ? 'el-icon-warning-outline' : 'el-icon-loading'"></i>
          </div>
          <span class="scan-tip" :class="{ done: bindStep === 'done', err: bindStep === 'error' }">
            {{ scanTipText }}
          </span>
        </div>
        <ol class="steps">
          <li>打开微信，扫描上方二维码</li>
          <li>在手机上确认登录授权</li>
          <li>授权成功后该微信号将出现在列表中</li>
        </ol>
        <div class="poll" :class="{ err: bindStep === 'error' }">
          <i class="el-icon-loading" v-if="bindStep === 'waiting' || bindStep === 'starting'"></i>
          <i class="el-icon-circle-check" v-else-if="bindStep === 'done'"></i>
          <i class="el-icon-warning-outline" v-else></i>
          {{ pollText }}
        </div>
      </div>
      <span slot="footer">
        <el-button round @click="bindVisible = false">关闭</el-button>
        <el-button v-if="bindStep === 'error'" type="primary" round @click="openBind">重试</el-button>
      </span>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog :title="detailAcc.nickname + ' · 详情'" :visible.sync="detailVisible" width="460px" custom-class="yw-dialog">
      <div class="detail-body" v-if="detailVisible">
        <div class="d-row"><span>微信号</span><b>{{ detailAcc.wechatId }}</b></div>
        <div class="d-row"><span>在线状态</span><b :class="detailAcc.status">{{ detailAcc.status === "online" ? "在线" : "离线" }}</b></div>
        <div class="d-row"><span>绑定时间</span><b>{{ detailAcc.boundAt }}</b></div>
        <div class="d-row"><span>最近活跃</span><b>{{ detailAcc.lastSeen }}</b></div>
        <div class="d-block">
          <label>绑定伴侣（开关即可绑定 / 解绑）</label>
          <div v-for="c in detailAcc.companionRows" :key="c.id" class="bind-item">
            <span>{{ c.name }}</span>
            <el-switch v-model="c.bound" active-color="#c94225" :disabled="bindBusy" @change="toggleBind(c)" />
          </div>
          <p v-if="!(detailAcc.companionRows && detailAcc.companionRows.length)" class="empty">还没有伴侣，请先创建伴侣</p>
        </div>
        <div class="d-block">
          <label>微信指令（聊天中可用）</label>
          <ul class="cmds">
            <li><code>/list</code> 列出此微信号绑定的所有伴侣</li>
            <li><code>/cur</code> 查看当前聊天对象正在使用的伴侣</li>
            <li><code>/su 伴侣id或名称</code> 切换当前活跃伴侣</li>
          </ul>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" round @click="detailVisible = false">知道了</el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<script>
import { wechatApi, companionApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "WechatAccountsView",
  components: { DashboardLayout },
  data() {
    return {
      accounts: [],
      allCompanions: [],
      bindVisible: false,
      bindStep: "waiting",
      bindQr: "",
      bindJobId: "",
      bindError: "",
      pollTimer: null,
      detailVisible: false,
      detailAcc: {},
      bindBusy: false,
      initialLoading: true,
    };
  },
  created() {
    this.loadInitial();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  computed: {
    scanTipText() {
      if (this.bindStep === "done") return "扫码成功，绑定完成";
      if (this.bindStep === "conflict") return "扫码授权成功";
      if (this.bindStep === "error") return "绑定未完成";
      return "请使用微信扫描二维码";
    },
    pollText() {
      if (this.bindStep === "done") return "绑定成功！";
      if (this.bindStep === "conflict") return "请确认是否将该微信号换绑到当前账号";
      if (this.bindStep === "error") return this.bindError || "微信网关未就绪，请稍后重试";
      if (this.bindStep === "starting") return "正在生成二维码…";
      return "正在等待扫码…";
    },
  },
  methods: {
    async loadInitial() {
      try {
        await Promise.all([this.load(), this.loadCompanions()]);
      } finally {
        this.initialLoading = false;
      }
    },
    async load() {
      try {
        const list = await wechatApi.list();
        this.accounts = (list || []).map((a) => ({
          id: a.id,
          nickname: a.nickname || a.wechat_account_id,
          wechatId: a.wechat_account_id,
          avatar: a.avatar_url || "",
          status: a.gateway_status === "online" ? "online" : "offline",
          boundAt: (a.created_at || "").slice(0, 10),
          lastSeen: (a.last_seen_at || "").replace("T", " ").slice(0, 16) || "—",
          companions: (a.bound_companions || []).map((c) => ({ id: c.id, name: c.name, isDefault: false, enabled: true })),
        }));
      } catch (e) {
        this.$message.warning("加载微信号失败");
      }
    },
    async loadCompanions() {
      try {
        const list = await companionApi.list();
        this.allCompanions = (list || []).map((c) => ({ id: c.id, name: c.name }));
      } catch (e) {
        this.allCompanions = [];
      }
    },
    async openBind() {
      this.stopPolling();
      this.bindStep = "starting";
      this.bindQr = "";
      this.bindJobId = "";
      this.bindError = "";
      this.bindVisible = true;
      try {
        const job = await wechatApi.createBindJob();
        this.applyJob(job);
        if (this.bindStep !== "done" && this.bindStep !== "error") this.startPolling();
      } catch (e) {
        this.bindStep = "error";
        this.bindError = e.message || "微信网关未就绪";
      }
    },
    applyJob(job) {
      if (!job) return;
      this.bindJobId = job.id || this.bindJobId;
      this.bindQr = job.qr_data_url || job.qr_url || "";
      if (job.status === "bound") {
        this.bindStep = "done";
        this.stopPolling();
        this.$message.success("微信号绑定成功");
        setTimeout(() => {
          this.bindVisible = false;
          this.load();
        }, 800);
      } else if (job.status === "failed" || job.status === "expired" || job.status === "conflict") {
        if (job.status === "conflict") {
          this.stopPolling();
          this.bindStep = "conflict";
          this.bindError = "";
          this.confirmBindReplace(job);
          return;
        }
        this.bindStep = "error";
        this.bindError = job.error_message || "绑定失败，请重试";
        this.stopPolling();
      } else if (job.status === "already_connected") {
        this.bindStep = "error";
        this.bindError = job.error_message || "该微信号已经连接，但未能确认账号身份，请刷新二维码重试";
        this.stopPolling();
      } else {
        this.bindStep = "waiting";
      }
    },
    async confirmBindReplace(job) {
      if (this.bindBusy) return;
      this.bindBusy = true;
      try {
        await this.$confirm(
          job.error_message || "该微信号已绑定到其他账号。继续操作将解除原账号中的微信号及伴侣关联。",
          "确认换绑微信号",
          {
            type: "warning",
            confirmButtonText: "确认换绑",
            cancelButtonText: "取消",
            distinguishCancelAndClose: true,
          }
        );
      } catch (e) {
        this.bindStep = "error";
        this.bindError = "已取消换绑，原账号的绑定保持不变";
        this.bindBusy = false;
        return;
      }
      try {
        const updated = await wechatApi.confirmBindReplace(job.id, job.wechat_account_id);
        this.applyJob(updated);
      } catch (e) {
        this.bindStep = "error";
        this.bindError = e.message || "换绑失败，请重试";
      } finally {
        this.bindBusy = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        if (!this.bindJobId) return;
        try {
          const job = await wechatApi.getBindJob(this.bindJobId);
          this.applyJob(job);
        } catch (e) {
          this.bindStep = "error";
          this.bindError = e.message || "查询绑定状态失败";
          this.stopPolling();
        }
      }, 2500);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    openDetail(acc) {
      const boundIds = new Set((acc.companions || []).map((c) => c.id));
      this.detailAcc = {
        ...acc,
        companionRows: this.allCompanions.map((c) => ({ id: c.id, name: c.name, bound: boundIds.has(c.id) })),
      };
      this.detailVisible = true;
    },
    async toggleBind(row) {
      if (this.bindBusy) return;
      this.bindBusy = true;
      const wechatId = this.detailAcc.wechatId;
      try {
        if (row.bound) {
          await companionApi.bindWechat(row.id, wechatId);
          this.$message.success(`已绑定到「${row.name}」`);
        } else {
          await companionApi.unbindWechat(row.id, wechatId);
          this.$message.success(`已解绑「${row.name}」`);
        }
        await this.load();
        const fresh = this.accounts.find((a) => a.wechatId === wechatId);
        if (fresh) this.openDetail(fresh);
      } catch (e) {
        row.bound = !row.bound;
        this.$message.error(e.message || "操作失败");
      } finally {
        this.bindBusy = false;
      }
    },
    confirmUnbind(acc) {
      this.$confirm(`确定要解绑「${acc.nickname}」吗？解绑后相关伴侣的微信绑定也会被清理。`, "解绑微信号", {
        type: "warning",
        confirmButtonText: "确定解绑",
        cancelButtonText: "取消",
      })
        .then(async () => {
          try {
            await wechatApi.remove(acc.wechatId);
            this.accounts = this.accounts.filter((a) => a.id !== acc.id);
            this.$message.success("已解绑");
          } catch (e) {
            this.$message.error(e.message || "解绑失败");
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped lang="scss">
.wx-page {
  .wx-empty {
    padding: 50px 0;
    text-align: center;
    color: #bbb;
    font-size: 14px;
  }

  .page-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;

    .title-block {
      h2 {
        margin: 0 0 6px;
        font-size: 26px;
        font-weight: 700;
        color: #1a1a1a;
      }

      p {
        margin: 0;
        color: #999;
        font-size: 13px;
      }
    }

    .add-btn {
      border: 0;
      background: linear-gradient(90deg, #c94225, #7255e9);

      i {
        margin-right: 4px;
      }
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;

    .acc-card {
      padding: 20px;
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
      transition: box-shadow 0.25s;

      &:hover {
        box-shadow: 0 12px 30px rgba(114, 85, 233, 0.12);
      }

      .acc-top {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .av {
          width: 46px;
          height: 46px;
          margin-right: 12px;
          border-radius: 12px;
          object-fit: cover;
          object-position: center 18%;
          background: #eeeafe;
        }

        .meta {
          flex: 1;

          b {
            display: block;
            font-size: 15px;
          }

          .wxid {
            color: #aaa;
            font-size: 12px;
          }
        }

        .status {
          font-size: 12px;

          &.online {
            color: #52c41a;
          }

          &.offline {
            color: #bbb;
          }
        }
      }

      .acc-info {
        display: flex;
        justify-content: space-between;
        margin-bottom: 14px;
        color: #aaa;
        font-size: 12px;
      }

      .bound {
        min-height: 64px;
        margin-bottom: 14px;
        padding: 12px;
        border-radius: 12px;
        background: #fafafa;

        label {
          display: block;
          margin-bottom: 8px;
          color: #999;
          font-size: 12px;
        }

        .chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;

          .chip {
            display: inline-flex;
            align-items: center;
            padding: 3px 10px;
            border-radius: 10px;
            color: #7255e9;
            font-size: 12px;
            background: #f7f4ff;

            em {
              margin-left: 4px;
              padding: 0 4px;
              border-radius: 6px;
              color: #fff;
              font-style: normal;
              font-size: 10px;
              background: #c8bdf6;
            }
          }
        }

        .empty {
          margin: 0;
          color: #bbb;
          font-size: 12px;
        }
      }

      .acc-btns {
        display: flex;
        gap: 8px;

        .el-button {
          flex: 1;
        }

        .danger {
          color: #ff6b6b;
          border-color: #ffd0d0;
        }
      }
    }
  }
}

.bind-body {
  text-align: center;

  .qr-box {
    .qr {
      width: 160px;
      height: 160px;
      border-radius: 12px;
      object-fit: contain;
      background: #f7f4f6;
    }

    .qr.placeholder {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #c8bdf6;
      font-size: 40px;
    }

    .scan-tip {
      display: block;
      margin: 10px 0 16px;
      color: #999;
      font-size: 13px;

      &.done {
        color: #52c41a;
      }

      &.err {
        color: #ff6b6b;
      }
    }
  }

  .steps {
    margin: 0 0 14px;
    padding-left: 20px;
    text-align: left;
    color: #777;
    font-size: 13px;

    li {
      margin-bottom: 6px;
    }
  }

  .poll {
    padding: 10px;
    border-radius: 10px;
    color: #7255e9;
    font-size: 13px;
    background: #f8f6fb;

    i {
      margin-right: 4px;
    }

    &.err {
      color: #ff6b6b;
      background: #fff1f0;
    }
  }
}

.detail-body {
  .d-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f5f0f2;
    font-size: 13px;

    span {
      color: #999;
    }

    b.online {
      color: #52c41a;
    }

    b.offline {
      color: #bbb;
    }
  }

  .d-block {
    margin-top: 16px;

    label {
      display: block;
      margin-bottom: 10px;
      color: #333;
      font-size: 13px;
      font-weight: 600;
    }

    .bind-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      font-size: 13px;

      em {
        margin-left: 4px;
        padding: 0 5px;
        border-radius: 6px;
        color: #fff;
        font-style: normal;
        font-size: 10px;
        background: #c8bdf6;
      }
    }

    .empty {
      margin: 0;
      color: #bbb;
      font-size: 13px;
    }

    .cmds {
      margin: 0;
      padding-left: 18px;
      color: #777;
      font-size: 13px;

      li {
        margin-bottom: 6px;
      }

      code {
        padding: 1px 6px;
        border-radius: 6px;
        color: #7255e9;
        background: #f7f4ff;
      }
    }
  }
}

@media (max-width: 768px) {
  .wx-page {
    .page-head {
      flex-wrap: wrap;
      gap: 10px;
    }

    .grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }
}
</style>

<style scoped lang="scss">
.wx-page {
  min-width: 0;

  .wx-empty,
  .page-head .title-block p,
  .grid .acc-card .acc-top .meta .wxid,
  .grid .acc-card .acc-info,
  .grid .acc-card .bound label,
  .grid .acc-card .bound .empty {
    color: var(--xyo-muted);
  }

  .page-head {
    min-width: 0;

    .title-block {
      min-width: 0;

      h2 {
        color: var(--xyo-ink-strong);
      }
    }

    .add-btn {
      background: var(--xyo-primary);
    }
  }

  .grid {
    min-width: 0;

    .acc-card {
      min-width: 0;
      border: 1px solid var(--xyo-line);
      background: var(--xyo-surface-raised);
      box-shadow: var(--xyo-shadow-sm);

      &:hover {
        box-shadow: var(--xyo-shadow-lg);
      }

      .acc-top {
        min-width: 0;

        .av {
          background: var(--xyo-accent-pale);
        }

        .meta {
          min-width: 0;
        }

        .status.online {
          color: var(--xyo-success);
        }

        .status.offline {
          color: var(--xyo-muted);
        }
      }

      .bound {
        border: 1px solid var(--xyo-line);
        background: var(--xyo-surface-muted);

        .chips .chip {
          color: var(--xyo-primary);
          background: var(--xyo-accent-pale);

          em {
            background: var(--xyo-primary);
          }
        }
      }

      .acc-btns .danger {
        color: var(--xyo-danger);
        border-color: var(--xyo-danger);
      }
    }
  }
}

.bind-body {
  min-width: 0;

  .qr-box {
    .qr {
      background: var(--xyo-surface-muted);
    }

    .qr.placeholder {
      color: var(--xyo-primary);
    }

    .scan-tip {
      color: var(--xyo-muted);

      &.done {
        color: var(--xyo-success);
      }

      &.err {
        color: var(--xyo-danger);
      }
    }
  }

  .steps {
    color: var(--xyo-ink-soft);
  }

  .poll {
    color: var(--xyo-primary);
    border: 1px solid var(--xyo-line);
    background: var(--xyo-accent-pale);

    &.err {
      color: var(--xyo-danger);
      background: var(--xyo-danger-soft);
    }
  }
}

.detail-body {
  min-width: 0;

  .d-row {
    min-width: 0;
    border-bottom-color: var(--xyo-line);

    span,
    b.offline {
      color: var(--xyo-muted);
    }

    b.online {
      color: var(--xyo-success);
    }
  }

  .d-block {
    label {
      color: var(--xyo-ink-strong);
    }

    .bind-item {
      min-width: 0;

      em {
        background: var(--xyo-primary);
      }
    }

    .empty,
    .cmds {
      color: var(--xyo-muted);
    }

    .cmds code {
      color: var(--xyo-primary);
      background: var(--xyo-accent-pale);
    }
  }
}

@media (max-width: 768px) {
  .wx-page {
    max-width: 100%;

    .page-head,
    .page-head .title-block,
    .grid,
    .grid .acc-card,
    .grid .acc-card .acc-top,
    .grid .acc-card .acc-info,
    .grid .acc-card .acc-btns {
      min-width: 0;
      max-width: 100%;
    }

    .grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .grid .acc-card .acc-btns {
      flex-wrap: wrap;
    }
  }

  .detail-body .d-row,
  .detail-body .d-block .bind-item {
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
